/* TWM hero slider (sections/twm-hero.liquid, styles in section-twm-homepage.css).

   The slides sit in a horizontally scroll-snapping track, so swiping works
   natively; this script glides it from slide to slide, keeps the dots in
   step and marks the slides that are off screen inert.

   Autoplay is driven by the active dot's fill animation: when it ends the
   next slide comes in. Pausing the animation therefore pauses the timer,
   which happens on hover, keyboard focus, a hidden tab or an off-screen
   hero (.is-held). Visitors who prefer reduced motion get no autoplay
   (no .is-playing) and move between slides with the dots or a swipe. */
(() => {
  const PROGRESS = 'twm-hero-progress'
  const GLIDE_MS = 1000
  const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)')
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)')

  const init = (root) => {
    if (root.twmSlider) return
    const track = root.querySelector('.twm-hero__track')
    const slides = track ? Array.from(track.children) : []
    const dots = Array.from(root.querySelectorAll('.twm-hero__dot'))
    if (slides.length < 2) return
    root.twmSlider = true

    let index = 0
    const holds = new Set()

    const setHold = (reason, on) => {
      if (on) holds.add(reason)
      else holds.delete(reason)
      root.classList.toggle('is-held', holds.size > 0)
    }

    const setPlaying = (playing) => root.classList.toggle('is-playing', playing)

    const setActive = (next) => {
      if (next === index && slides[next].classList.contains('is-active')) return
      const entering = next !== index
      index = next
      slides.forEach((slide, n) => {
        const active = n === next
        slide.classList.toggle('is-active', active)
        slide.inert = !active
        if (active && entering) {
          // Replays the copy's entrance (twm-motion.css) on the slide that arrives.
          slide.classList.remove('is-entering')
          void slide.offsetWidth
          slide.classList.add('is-entering')
        }
      })
      dots.forEach((dot, n) => {
        dot.classList.toggle('is-active', n === next)
        if (n === next) dot.setAttribute('aria-current', 'true')
        else dot.removeAttribute('aria-current')
      })
    }

    // An eased glide instead of the browser's quick smooth scroll. Snapping
    // is off while it runs (.is-gliding), or every frame would snap back.
    let glide = null
    let glideSafety = null
    const stopGlide = () => {
      if (glide) cancelAnimationFrame(glide)
      clearTimeout(glideSafety)
      glide = null
      root.classList.remove('is-gliding')
    }
    const glideTo = (left) => {
      stopGlide()
      const from = track.scrollLeft
      const distance = left - from
      if (reduce.matches || Math.abs(distance) < 2) {
        track.scrollLeft = left
        return
      }
      const start = performance.now()
      root.classList.add('is-gliding')
      const frame = (now) => {
        const t = Math.min((now - start) / GLIDE_MS, 1)
        track.scrollLeft = from + distance * easeInOut(t)
        if (t < 1) glide = requestAnimationFrame(frame)
        else stopGlide()
      }
      glide = requestAnimationFrame(frame)
      // If frames stop (a background tab), land on the slide anyway.
      glideSafety = setTimeout(() => {
        stopGlide()
        track.scrollLeft = left
      }, GLIDE_MS + 300)
    }
    ;['pointerdown', 'wheel', 'touchstart'].forEach((type) => track.addEventListener(type, stopGlide, { passive: true }))

    const go = (next) => {
      const target = (next + slides.length) % slides.length
      glideTo(target * track.clientWidth)
      setActive(target)
    }

    // Whichever slide is mostly in view is the active one (swipes included).
    const seen = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) setActive(slides.indexOf(entry.target))
        })
      },
      { root: track, threshold: 0.6 }
    )
    slides.forEach((slide) => seen.observe(slide))

    // Hold while the hero is mostly scrolled away.
    new IntersectionObserver(([entry]) => setHold('offscreen', !entry.isIntersecting), { threshold: 0.25 }).observe(root)

    dots.forEach((dot, n) => dot.addEventListener('click', () => go(n)))

    root.addEventListener('animationend', (event) => {
      if (event.animationName === PROGRESS) go(index + 1)
    })

    root.addEventListener('pointerenter', () => finePointer.matches && setHold('hover', true))
    root.addEventListener('pointerleave', () => setHold('hover', false))
    root.addEventListener('focusin', (event) => {
      if (event.target.matches(':focus-visible')) setHold('focus', true)
    })
    root.addEventListener('focusout', (event) => {
      if (!root.contains(event.relatedTarget)) setHold('focus', false)
    })
    document.addEventListener('visibilitychange', () => setHold('hidden', document.hidden))

    // Keep the active slide in place when the width changes.
    window.addEventListener('resize', () => {
      stopGlide()
      track.scrollLeft = index * track.clientWidth
    })

    slides.forEach((slide, n) => (slide.inert = n !== 0))
    setPlaying(!reduce.matches && !(window.Shopify && Shopify.designMode))

    // Theme editor: show the slide being edited.
    root.addEventListener('shopify:block:select', (event) => {
      const n = slides.indexOf(event.target)
      if (n > -1) {
        setPlaying(false)
        go(n)
      }
    })
  }

  const start = () => document.querySelectorAll('[data-twm-hero-slider]').forEach(init)
  start()
  document.addEventListener('shopify:section:load', start)
})()
